/**
 * ModuleLockBoundary — wraps the page area inside AppLayout.
 *
 * 1. Tells ProtectedRoute it is already inside the app chrome
 *    (InsideAppLayoutContext), so a locked nested route renders the lock
 *    screen in place rather than a second AppLayout.
 * 2. Swaps the page being shown for <ModuleLocked> when any request it makes
 *    comes back 403 module_not_licensed (licensing/moduleLockEvents). That
 *    covers pages route gating cannot see — an Atlas page that calls an
 *    endpoint pinned to one module, for instance. The swap lasts until the
 *    reader navigates; the page is unmounted, so it stops fetching.
 *
 * With ui_module_lock off (the auth service's off-switch) or for a super admin,
 * events are ignored and the page handles the 403 as it always did.
 *
 * Only pages that belong to a licensable module are swapped. On a core page
 * (Atlas, Policy) or an ungated one, a 403 from one widget — e.g. an Atlas
 * view filtered to a surface the org hasn't bought — must not blank the whole
 * page; that widget shows its own error instead.
 */
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ModuleLocked from './ModuleLocked';
import { MODULE_NOT_LICENSED_EVENT } from '../../licensing/moduleLockEvents';
import { getModuleLicense } from '../../licensing/useModuleLicense';
import { moduleForNavPath } from '../../licensing/navModules';

export const InsideAppLayoutContext = createContext(false);

export default function ModuleLockBoundary({ children }) {
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  const [locked, setLocked] = useState(null); // { module, at }
  const hereRef = useRef(here);
  hereRef.current = here;

  useEffect(() => {
    const onDenied = (e) => {
      const license = getModuleLicense();
      if (!license.enforcing) return;
      const pageModules = [].concat(moduleForNavPath(hereRef.current) || []);
      if (pageModules.length === 0 || pageModules.every((m) => license.isCore(m))) return;
      // Even when the token says the module is licensed, the service's 403 is
      // the authority (a stale token, most likely): lock anyway.
      const module = e?.detail?.module || null;
      setLocked({ module, at: hereRef.current });
    };
    window.addEventListener(MODULE_NOT_LICENSED_EVENT, onDenied);
    return () => window.removeEventListener(MODULE_NOT_LICENSED_EVENT, onDenied);
  }, []);

  // A navigation clears the swap: the next page gets its own chance.
  useEffect(() => {
    setLocked((cur) => (cur && cur.at !== here ? null : cur));
  }, [here]);

  return (
    <InsideAppLayoutContext.Provider value>
      {locked && locked.at === here ? <ModuleLocked module={locked.module} /> : children}
    </InsideAppLayoutContext.Provider>
  );
}
